import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Stack,
  Tooltip,
  Box,
} from "@mui/material";
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-premium";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAuth } from "../auth/useAuth";
import { loadLocations } from "../api/scheduleApi";
import { fetchPublishedSchedules } from "../api/publishApi";
import { getCurrentWeekDates } from "../utils/dateUtils"; // ✅ added

const CustomToolbar = () => (
  <GridToolbarContainer sx={{ justifyContent: "flex-end", p: 1 }}>
    <GridToolbarQuickFilter
      placeholder="Search..."
      sx={{
        width: 240,
        "& .MuiInputBase-input": { fontSize: 14 },
      }}
    />
  </GridToolbarContainer>
);

const PublishedSchedulesPage = () => {
  const { getAccessToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    location: "",
    week_start: "",
    week_end: "",
  });

  // ----------------------------
  // Default to current week
  // ----------------------------
  useEffect(() => {
    const { formattedStart, formattedEnd } = getCurrentWeekDates();
    setFilters((prev) => ({
      ...prev,
      week_start: formattedStart,
      week_end: formattedEnd,
    }));
  }, []);

  // ----------------------------
  // Load Locations
  // ----------------------------
  useEffect(() => {
    const loadLocs = async () => {
      try {
        const token = await getAccessToken();
        const locs = await loadLocations(token);
        setLocations(locs);
      } catch (err) {
        console.error("❌ Failed to load locations:", err);
      }
    };
    loadLocs();
  }, []);

  // ----------------------------
  // Fetch Data
  // ----------------------------
  const fetchData = async () => {
    if (!filters.week_start || !filters.week_end) return;
    setLoading(true);
    try {
      const token = await getAccessToken();
      const data = await fetchPublishedSchedules(filters, token);
      setRows(data);
    } catch (err) {
      console.error("❌ Error fetching published schedules:", err);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Columns
  // ----------------------------
  const columns = [
    {
      field: "revision",
      headerName: "Revision",
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.row.is_latest ? "success.light" : "grey.200",
            color: params.row.is_latest ? "success.dark" : "text.primary",
            fontWeight: 600,
            fontSize: 14,
            textAlign: "center",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "published_by",
      headerName: "Published By",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "published_at",
      headerName: "Published At",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const d = new Date(params.value);
        return d.toLocaleString();
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View PDF">
          <IconButton
            color="primary"
            href={params.row.presigned_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <Box sx={{ p: 3, bgcolor: "#f7f9fb", minHeight: "100vh" }}>
      {/* Header + Filters */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Published Schedules
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          View previously published weekly schedules by location and date range.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: "center",
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <TextField
            select
            label="Location"
            value={filters.location}
            onChange={(e) =>
              setFilters({ ...filters, location: e.target.value })
            }
            sx={{ minWidth: 200 }}
          >
            {locations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Week Start"
            type="date"
            value={filters.week_start}
            onChange={(e) =>
              setFilters({ ...filters, week_start: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Week End"
            type="date"
            value={filters.week_end}
            onChange={(e) =>
              setFilters({ ...filters, week_end: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />

          <Button
            variant="contained"
            onClick={fetchData}
            disabled={!filters.week_start || !filters.week_end || loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Load"
            )}
          </Button>
        </Stack>
      </Paper>

      {/* Data Table */}
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "40vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGridPremium
            rows={rows.map((r, i) => ({ id: i + 1, ...r }))}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            density="compact"
            slots={{
              toolbar: CustomToolbar,
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f0f3f9",
                fontWeight: 600,
                fontSize: "0.9rem",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#fafafa",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default PublishedSchedulesPage;
